import { Resource } from './resource';
import { CaptureGroup, MultiPointInstallation, Organization } from './api';
import { Filter } from '../types/filters';
import { Sort, SortDirection, SortColumn } from '../types/sort';

export type HighlightableM2MCaptureGroup = CaptureGroup & {
  highlighted: boolean;
};

export type MultiPointInstallationM2MCapturesState = {
  page: number;

  filters: Array<Filter>;
  sort: Sort;

  resource: Resource<{
    mpi: MultiPointInstallation;
    captureGroups: {
      total: number;
      total_accuracy: number;
      groups: Array<HighlightableM2MCaptureGroup>;
      min_video_starts_at: string;
    };
    selectedCaptureGroupId: CaptureGroup['id'] | null;
    seek: number;

    fetchingMoreMPIs: Resource<null>; // There is no data in this resource, it's only used as a status
  }>;
};

export enum MultiPointInstallationM2MCapturesActionTypes {
  MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_PAGE = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_PAGE',
  MULTI_POINT_INSTALLATION_M2M_CAPTURES_LEAVE_PAGE = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_LEAVE_PAGE',

  MULTI_POINT_INSTALLATION_M2M_CAPTURES_SELECT_CAPTURE_GROUP = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_SELECT_CAPTURE_GROUP',
  MULTI_POINT_INSTALLATION_M2M_CAPTURES_SELECTED_CAPTURE_VIEWER_SEEK = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_SELECTED_CAPTURE_VIEWER_SEEK',
  MULTI_POINT_INSTALLATION_M2M_CAPTURES_PUSH_NEW = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_PUSH_NEW',
  MULTI_POINT_INSTALLATION_M2M_CAPTURES_CLEAR_HIGHLIGHT = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_CLEAR_HIGHLIGHT',

  MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_FILTERS = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_FILTERS',
  MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_SORT = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_SORT',

  MULTI_POINT_INSTALLATION_M2M_CAPTURES_LOADING = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_LOADING',
  MULTI_POINT_INSTALLATION_M2M_CAPTURES_ERROR = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_ERROR',
  MULTI_POINT_INSTALLATION_M2M_CAPTURES_LOAD_COMPLETE = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_LOAD_COMPLETE',

  MULTI_POINT_INSTALLATION_M2M_CAPTURES_SET_PAGE = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_SET_PAGE',
  MULTI_POINT_INSTALLATION_M2M_CAPTURES_NEXT_PAGE_LOAD_COMPLETE = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_NEXT_PAGE_LOAD_COMPLETE',
  MULTI_POINT_INSTALLATION_M2M_CAPTURES_NEXT_PAGE_LOAD_ERROR = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_NEXT_PAGE_LOAD_ERROR',

  MULTI_POINT_INSTALLATION_M2M_CAPTURES_DELETE_CAPTURE_GROUPS = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_DELETE_CAPTURE_GROUPS',
  MULTI_POINT_INSTALLATION_M2M_CAPTURES_DELETE_CAPTURE_GROUPS_COMPLETE = 'MULTI_POINT_INSTALLATION_M2M_CAPTURES_DELETE_CAPTURE_GROUPS_COMPLETE',
}

export type MultiPointInstallationM2MCapturesAction =
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_PAGE;
      id: MultiPointInstallation['id'];
      params: {
        capture_id?: number;
        t?: number;
      };
      filters: Array<Filter>;
      sort: Sort;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_LEAVE_PAGE;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_FILTERS;
      filters: Array<Filter>;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CHANGE_SORT;
      direction: SortDirection;
      column: SortColumn;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_LOADING;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_ERROR;
      error: any;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_SELECT_CAPTURE_GROUP;
      id: CaptureGroup['id'];
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_LOAD_COMPLETE;
      mpi: MultiPointInstallation;
      captureGroups: {
        capture_groups_count: number;
        total_accuracy: number;
        groups: Array<CaptureGroup>;
        min_video_starts_at: string;
      };
      selectedCaptureGroupId: CaptureGroup['id'] | null;
      seek: number;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_SELECTED_CAPTURE_VIEWER_SEEK;
      seek: number;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_PUSH_NEW;
      totalAccuracy: number;
      captureGroups: Array<CaptureGroup>;
      totalCaptureGroups: number;
      minVideoStartsAt: string;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_SET_PAGE;
      value: number;
      mpiId: MultiPointInstallation['id'];
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_NEXT_PAGE_LOAD_ERROR;
      error: any;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_NEXT_PAGE_LOAD_COMPLETE;
      captureGroups: {
        total: number;
        total_accuracy: number;
        groups: Array<CaptureGroup>;
      };
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_CLEAR_HIGHLIGHT;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_DELETE_CAPTURE_GROUPS;
      mpiId: MultiPointInstallation['id'];
      ids: Array<CaptureGroup['id']>;
    }
  | {
      type: MultiPointInstallationM2MCapturesActionTypes.MULTI_POINT_INSTALLATION_M2M_CAPTURES_DELETE_CAPTURE_GROUPS_COMPLETE;
      ids: Array<CaptureGroup['id']>;
    };
